/**
 * useAppNavigation — Custom hook wrapping app store for navigation
 * Provides convenient, type-safe navigation actions and state access
 */

import { useCallback } from 'react';
import { useNavigation } from '../stores/useAppStore';
import { getAppConfig, getPageConfig } from '../services/appRegistry';
import type { AppConfig, PageConfig } from '../types';

interface UseAppNavigationReturn {
  currentApp: string;
  currentPage: string;
  currentSubPage: string | null;
  currentLevel: 'waffle' | 'app' | 'page';
  canNavigate: (appId: string, pageId: string) => boolean;
  navigateTo: (appId: string, pageId: string, subPageId?: string | null) => void;
  navigateToFixedTab: (tabId: string) => void;
  switchTo: (appId: string) => void;
  getCurrentAppConfig: () => AppConfig | null;
  getCurrentPageConfig: () => PageConfig | null;
}

export const useAppNavigation = (): UseAppNavigationReturn => {
  const {
    navigation,
    navigateToPage,
    switchApp,
  } = useNavigation();

  const canNavigate = useCallback((appId: string, pageId: string): boolean => {
    const app = getAppConfig(appId);
    if (!app) return false;
    return pageId in app.pages;
  }, []);

  const navigateTo = useCallback((appId: string, pageId: string, subPageId?: string | null) => {
    if (canNavigate(appId, pageId)) {
      navigateToPage(appId, pageId, subPageId || null);
    }
  }, [canNavigate, navigateToPage]);

  const navigateToFixedTab = useCallback((tabId: string) => {
    // Fixed tabs like '__today__' and '__home__' are special pages in Workspace
    const workspaceApp = 'tru-clara-ai';
    navigateToPage(workspaceApp, tabId, null);
  }, [navigateToPage]);

  const switchTo = useCallback((appId: string) => {
    if (getAppConfig(appId)) {
      switchApp(appId);
    }
  }, [switchApp]);

  const getCurrentAppConfig = useCallback((): AppConfig | null => {
    return getAppConfig(navigation.currentApp);
  }, [navigation.currentApp]);

  const getCurrentPageConfig = useCallback((): PageConfig | null => {
    return getPageConfig(navigation.currentApp, navigation.currentPage);
  }, [navigation.currentApp, navigation.currentPage]);

  return {
    currentApp: navigation.currentApp,
    currentPage: navigation.currentPage,
    currentSubPage: navigation.currentSubPage,
    currentLevel: navigation.currentLevel,
    canNavigate,
    navigateTo,
    navigateToFixedTab,
    switchTo,
    getCurrentAppConfig,
    getCurrentPageConfig,
  };
};
